/**
 * Faltantes y lista de compras (Req. 14).
 *
 * Es el MISMO calculo que la produccion, visto desde el otro lado:
 *
 *   produccion  -> "para este platillo necesito 800 g de queso, tengo 700"
 *   lista       -> "para toda la semana necesito 2 kg de queso, me faltan 300 g"
 *
 * Por eso ambos salen de `necesidadesPorInsumo` en el dominio y no de dos
 * calculos paralelos: dos implementaciones acabarian dando numeros distintos en
 * dos pantallas, que es exactamente el error que destruye la confianza.
 *
 * Se filtra por fecha de PREPARACION, no de venta: los insumos se necesitan el
 * dia que se cocina. Filtrar por venta mandaria a comprar el lunes lo que habia
 * que cocinar el domingo.
 */

import { db } from '../schema';
import { nuevoId } from '../ids';
import { ahoraIso, hoy, type FechaLocal } from '@/domain/dates';
import {
  necesidadesPorInsumo,
  calcularFaltantes,
  sugerenciaDeCompra,
  planeadosDelPeriodo,
  rangoDelPeriodo,
  type PeriodoLista,
  type RecetaParaPlan,
  type SugerenciaCompra,
  type OrigenNecesidad,
} from '@/domain/planning';
import { formatearCantidadBase, etiquetaUnidad } from '@/domain/units';
import type { Centavos } from '@/domain/money';
import type { Ingredient, PlannedItem, ShoppingCheck } from '../types';
import { costoPorBase } from '@/domain/inventory';
import { estadoDe } from './ingredients';

// ---------------------------------------------------------------------------

export interface RenglonCompra {
  ingredientId: string;
  nombreInsumo: string;
  /** Lo que piden todos los platillos del periodo, en unidad legible. */
  necesarioLegible: string;
  necesarioBase: number;
  /** Lo que hay en inventario ahora. */
  disponibleLegible: string;
  disponibleBase: number;
  /** Lo que hay que conseguir. Cero cuando alcanza. */
  faltanteLegible: string;
  faltanteBase: number;
  alcanza: boolean;
  /** Cuantos paquetes comprar. null si el insumo no declara equivalencia. */
  sugerencia: SugerenciaCompra | null;
  sugerenciaLegible: string | null;
  /** Costo aproximado del faltante, con el ultimo costo conocido. */
  costoAproximadoCentavos: Centavos | null;
  /** Platillos que lo piden, para poder explicar el renglon. */
  origenes: OrigenNecesidad[];
  /** true si ya se marco como atendido en este periodo. */
  atendido: boolean;
}

export interface ListaDeCompras {
  periodo: PeriodoLista;
  desde: FechaLocal;
  hasta: FechaLocal;
  /** Solo lo que hace falta comprar. */
  faltantes: RenglonCompra[];
  /** Lo que se necesita pero ya se tiene. Se muestra aparte, no se esconde. */
  cubiertos: RenglonCompra[];
  /** Suma de los costos aproximados que se pudieron estimar. */
  costoAproximadoCentavos: Centavos;
  /** true si algun faltante no tiene costo conocido: el total es parcial. */
  costoIncompleto: boolean;
  /** Platillos considerados, para que la pantalla pueda decir de donde sale. */
  platillosConsiderados: number;
}

/**
 * Arma la lista de compras de un periodo.
 *
 * Un solo renglon por insumo, aunque lo pidan tres platillos distintos
 * (Req. 14.2). El desglose queda en `origenes` para poder revisarlo.
 */
export async function verListaDeCompras(
  periodo: PeriodoLista = 'semana',
  fecha: FechaLocal = hoy(),
): Promise<ListaDeCompras> {
  const { desde, hasta } = rangoDelPeriodo(periodo, fecha);

  const todos = await db.plannedItems.toArray();
  const platillos = planeadosDelPeriodo(todos, periodo, fecha);

  // Lo ya cocinado no vuelve a pedir insumos: ya se descontaron.
  const producidos = new Set(
    (await db.productions.toArray())
      .filter((p) => p.estado === 'completada')
      .map((p) => p.plannedItemId),
  );
  const pendientes = platillos.filter((p) => !producidos.has(p.id));

  const recetas = await mapaDeRecetas(pendientes);
  const necesidades = necesidadesPorInsumo(pendientes, recetas);

  const insumos = new Map(
    (await db.ingredients.bulkGet(necesidades.map((n) => n.ingredientId)))
      .filter((i): i is Ingredient => i !== undefined)
      .map((i) => [i.id, i]),
  );

  const faltantesCrudos = calcularFaltantes(
    necesidades,
    (id) => insumos.get(id)?.stockBase ?? 0,
  );

  const clave = claveDePeriodo(periodo, fecha);
  const marcados = new Set(
    (await db.shoppingChecks.where('periodoClave').equals(clave).toArray())
      .filter((c) => c.atendido)
      .map((c) => c.ingredientId),
  );

  const renglones: RenglonCompra[] = faltantesCrudos.map((faltante) => {
    const insumo = insumos.get(faltante.ingredientId);

    if (insumo === undefined) {
      return {
        ingredientId: faltante.ingredientId,
        nombreInsumo: '(insumo eliminado)',
        necesarioLegible: String(faltante.necesarioBase),
        necesarioBase: faltante.necesarioBase,
        disponibleLegible: '0',
        disponibleBase: 0,
        faltanteLegible: String(faltante.faltanteBase),
        faltanteBase: faltante.faltanteBase,
        alcanza: faltante.alcanza,
        sugerencia: null,
        sugerenciaLegible: null,
        costoAproximadoCentavos: null,
        origenes: faltante.origenes,
        atendido: marcados.has(faltante.ingredientId),
      };
    }

    const legible = (cantidad: number): string =>
      formatearCantidadBase(cantidad, insumo.dimension, insumo.unidadCompraPreferida);

    const sugerencia = sugerenciaDeCompra(faltante.faltanteBase, insumo.factorEquivalencia);
    const { costoPorBase: unitario } = costoPorBase(estadoDe(insumo));

    return {
      ingredientId: insumo.id,
      nombreInsumo: insumo.nombre,
      necesarioLegible: legible(faltante.necesarioBase),
      necesarioBase: faltante.necesarioBase,
      disponibleLegible: legible(faltante.disponibleBase),
      disponibleBase: faltante.disponibleBase,
      faltanteLegible: legible(faltante.faltanteBase),
      faltanteBase: faltante.faltanteBase,
      alcanza: faltante.alcanza,
      sugerencia,
      sugerenciaLegible:
        sugerencia === null
          ? null
          : `${sugerencia.cantidad} ${etiquetaUnidad(sugerencia.unidad, sugerencia.cantidad)}`,
      costoAproximadoCentavos:
        unitario === null ? null : Math.round(faltante.faltanteBase * unitario),
      origenes: faltante.origenes,
      atendido: marcados.has(insumo.id),
    };
  });

  const porNombre = (a: RenglonCompra, b: RenglonCompra): number =>
    a.nombreInsumo.localeCompare(b.nombreInsumo, 'es');

  const faltantes = renglones.filter((r) => !r.alcanza).sort(porNombre);
  const cubiertos = renglones.filter((r) => r.alcanza).sort(porNombre);

  return {
    periodo,
    desde,
    hasta,
    faltantes,
    cubiertos,
    costoAproximadoCentavos: faltantes.reduce(
      (suma, r) => suma + (r.costoAproximadoCentavos ?? 0),
      0,
    ),
    costoIncompleto: faltantes.some((r) => r.costoAproximadoCentavos === null),
    platillosConsiderados: pendientes.length,
  };
}

/** Recetas de los platillos, reducidas a lo que el dominio necesita. */
async function mapaDeRecetas(
  platillos: readonly PlannedItem[],
): Promise<Map<string, RecetaParaPlan>> {
  const ids = [...new Set(platillos.map((p) => p.recipeId))];
  if (ids.length === 0) return new Map();

  const recetas = (await db.recipes.bulkGet(ids)).filter((r) => r !== undefined);
  const items = await db.recipeItems.where('recipeId').anyOf(ids).toArray();

  const mapa = new Map<string, RecetaParaPlan>();
  for (const receta of recetas) {
    mapa.set(receta.id, {
      rendimientoPorciones: receta.rendimientoPorciones,
      items: items
        .filter((i) => i.recipeId === receta.id)
        .map((i) => ({
          ingredientId: i.ingredientId,
          cantidadBase: i.cantidadBase,
          escala: i.escala,
        })),
    });
  }
  return mapa;
}

// ---------------------------------------------------------------------------
// Marcado de renglones
// ---------------------------------------------------------------------------

/**
 * Clave del periodo, para que marcar un renglon de esta semana no aparezca
 * marcado la semana que entra (Req. 14.11).
 */
export function claveDePeriodo(periodo: PeriodoLista, fecha: FechaLocal = hoy()): string {
  return `${periodo}|${fecha}`;
}

export async function marcarAtendido(
  ingredientId: string,
  periodo: PeriodoLista,
  fecha: FechaLocal = hoy(),
  atendido = true,
): Promise<void> {
  const clave = claveDePeriodo(periodo, fecha);

  const existente = (await db.shoppingChecks.where('ingredientId').equals(ingredientId).toArray())
    .find((c) => c.periodoClave === clave);

  if (existente !== undefined) {
    await db.shoppingChecks.update(existente.id, { atendido });
    return;
  }

  const marca: ShoppingCheck = {
    id: nuevoId('chk'),
    ingredientId,
    periodoClave: clave,
    atendido,
    creadoEn: ahoraIso(),
  };
  await db.shoppingChecks.add(marca);
}

/** Limpia las marcas de un periodo, para empezar la lista de nuevo. */
export async function limpiarMarcas(
  periodo: PeriodoLista,
  fecha: FechaLocal = hoy(),
): Promise<void> {
  const clave = claveDePeriodo(periodo, fecha);
  const marcas = (await db.shoppingChecks.toArray()).filter((c) => c.periodoClave === clave);
  await db.shoppingChecks.bulkDelete(marcas.map((m) => m.id));
}

// ---------------------------------------------------------------------------
// Resumen para el dashboard
// ---------------------------------------------------------------------------

export interface ResumenFaltantes {
  cuantos: number;
  costoAproximadoCentavos: Centavos;
  costoIncompleto: boolean;
  /** Los primeros nombres, para un aviso corto. */
  primeros: string[];
}

export async function resumenDeFaltantes(
  periodo: PeriodoLista = 'proximos',
  fecha: FechaLocal = hoy(),
): Promise<ResumenFaltantes> {
  const lista = await verListaDeCompras(periodo, fecha);
  const pendientes = lista.faltantes.filter((r) => !r.atendido);

  return {
    cuantos: pendientes.length,
    costoAproximadoCentavos: pendientes.reduce(
      (suma, r) => suma + (r.costoAproximadoCentavos ?? 0),
      0,
    ),
    costoIncompleto: pendientes.some((r) => r.costoAproximadoCentavos === null),
    primeros: pendientes.slice(0, 3).map((r) => r.nombreInsumo),
  };
}
